import { collection, doc, getDoc, setDoc, getDocs, query, where, orderBy, limit, Timestamp, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getUserById, updateUserData, logAdminActivity } from '../firebase/helpers';
import { verifyAdminAccess } from './adminService';

const MANUAL_KNIFE_ASSIGNMENTS_COLLECTION = 'manualKnifeAssignments';

/**
 * Assign a manual knife to a user
 * The knife will be applied when the user starts optimization
 * @param {string} userId - User ID
 * @param {number} knifeAmount - Amount of knife to apply
 * @param {string} reason - Reason for assignment (optional)
 * @returns {Promise<Object>}
 */
export const assignManualKnife = async (userId, knifeAmount, reason = '') => {
  try {
    const adminInfo = await verifyAdminAccess();
    
    // Validate inputs
    if (!userId || !knifeAmount || knifeAmount <= 0) {
      throw new Error('Invalid assignment parameters: userId and knifeAmount are required');
    }
    
    // Get user to validate
    const user = await getUserById(userId);
    if (!user) {
      throw new Error('User not found');
    }
    
    // CRITICAL RULE: Check if user already has an ACTIVE knife
    // A user can only have ONE active knife at a time
    // Check 1: User object fields
    if (user.isKnifeApplied || user.hasNegativeBalance || (user.balances?.negative && user.balances.negative > 0)) {
      throw new Error(
        `User already has an active knife applied. ` +
        `Please remove the current knife before assigning a new one. ` +
        `Current knife amount: ${user.balances?.knifeAmount || 'N/A'} USDT`
      );
    }
    
    // Check 2: Active knife transactions in database
    const knifeTransactionsRef = collection(db, 'knifeTransactions');
    const activeKnifeQuery = query(
      knifeTransactionsRef,
      where('userId', '==', userId),
      where('status', '==', 'active')
    );
    const activeKnifeSnapshot = await getDocs(activeKnifeQuery);
    if (!activeKnifeSnapshot.empty) {
      const activeKnife = activeKnifeSnapshot.docs[0].data();
      throw new Error(
        `User already has an active knife transaction. ` +
        `Please remove the current knife before assigning a new one. ` +
        `Current knife amount: ${activeKnife.knifeAmount || 'N/A'} USDT`
      );
    }
    
    // Check 3: Applied assignments (not just pending)
    const assignmentsRef = collection(db, MANUAL_KNIFE_ASSIGNMENTS_COLLECTION);
    const appliedAssignmentQuery = query(
      assignmentsRef,
      where('userId', '==', userId),
      where('status', '==', 'applied')
    );
    const appliedAssignmentSnapshot = await getDocs(appliedAssignmentQuery);
    if (!appliedAssignmentSnapshot.empty) {
      const appliedAssignment = appliedAssignmentSnapshot.docs[0].data();
      throw new Error(
        `User already has an applied knife assignment. ` +
        `Please remove the current knife before assigning a new one. ` +
        `Current knife amount: ${appliedAssignment.knifeAmount || 'N/A'} USDT`
      );
    }
    
    // Check if assignment already exists for this user (pending status)
    // Reuse assignmentsRef from above (same collection)
    const q = query(
      assignmentsRef,
      where('userId', '==', userId),
      where('status', '==', 'pending')
    );
    const existingSnapshot = await getDocs(q);
    
    if (!existingSnapshot.empty) {
      // Update existing PENDING assignment (not applied yet)
      const existingDoc = existingSnapshot.docs[0];
      const existingData = existingDoc.data();
      
      await updateDoc(doc(db, MANUAL_KNIFE_ASSIGNMENTS_COLLECTION, existingDoc.id), {
        knifeAmount: parseFloat(knifeAmount),
        reason: reason || existingData.reason,
        updatedAt: Timestamp.now(),
        updatedBy: adminInfo.adminId,
        updatedByEmail: adminInfo.adminEmail
      });
      
      await logAdminActivity({
        adminId: adminInfo.adminId,
        adminEmail: adminInfo.adminEmail,
        action: 'updateManualKnife',
        targetUserId: userId,
        targetUserEmail: user.email,
        changes: {
          knifeAmount,
          previousAmount: existingData.knifeAmount
        },
        description: `Updated pending manual knife assignment for ${user.email || userId}: Amount ${knifeAmount} USDT`
      });
      
      return {
        success: true,
        assignmentId: existingDoc.id,
        action: 'updated',
        assignment: {
          id: existingDoc.id,
          userId,
          knifeAmount: parseFloat(knifeAmount),
          status: 'pending',
          reason
        }
      };
    }
    
    // Create new assignment
    const assignmentRef = doc(collection(db, MANUAL_KNIFE_ASSIGNMENTS_COLLECTION));
    const assignmentId = assignmentRef.id;
    
    const assignmentDoc = {
      id: assignmentId,
      userId: userId,
      userEmail: user.email || '',
      knifeAmount: parseFloat(knifeAmount),
      status: 'pending', // pending, applied, cancelled
      reason: reason || '',
      assignedAt: Timestamp.now(),
      assignedBy: adminInfo.adminId,
      assignedByEmail: adminInfo.adminEmail,
      appliedAt: null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    await setDoc(assignmentRef, assignmentDoc);
    
    // Log admin activity
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'assignManualKnife',
      targetUserId: userId,
      targetUserEmail: user.email,
      changes: {
        knifeAmount
      },
      description: `Assigned manual knife to ${user.email || userId}: Amount ${knifeAmount} USDT${reason ? ` - ${reason}` : ''}`
    });
    
    // Create notification for user
    try {
      const notificationRef = doc(collection(db, 'notifications'));
      await setDoc(notificationRef, {
        userId: userId,
        type: 'warning',
        title: '⚠️ Manual Knife Assignment',
        message: `A knife of ${knifeAmount} USDT has been assigned to you. It will automatically apply when you start optimization.`,
        read: false,
        createdAt: Timestamp.now()
      });
    } catch (notifError) {
      console.error('Error creating notification:', notifError);
    }
    
    console.log(`✅ Manual knife assignment created: User ${userId}, Amount ${knifeAmount} USDT`);
    
    return {
      success: true,
      assignmentId,
      action: 'created',
      assignment: assignmentDoc
    };
  } catch (error) {
    console.error('Error assigning manual knife:', error);
    throw error;
  }
};

/**
 * Get all manual knife assignments (admin only)
 * @returns {Promise<Array>}
 */
export const getAllManualKnifeAssignments = async () => {
  try {
    await verifyAdminAccess();
    
    const assignmentsRef = collection(db, MANUAL_KNIFE_ASSIGNMENTS_COLLECTION);
    const q = query(assignmentsRef, orderBy('assignedAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const assignments = [];
    for (const docSnap of snapshot.docs) {
      const assignment = docSnap.data();
      // Get user info
      const user = await getUserById(assignment.userId).catch(() => null);
      assignments.push({
        ...assignment,
        user: user ? {
          email: user.email,
          username: user.username,
          vipLevel: user.vipLevel
        } : null
      });
    }
    
    return assignments;
  } catch (error) {
    console.error('Error getting all manual knife assignments:', error);
    throw error;
  }
};

/**
 * Get manual knife assignment for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>}
 */
export const getUserManualKnifeAssignment = async (userId) => {
  try {
    if (!userId) {
      console.error('getUserManualKnifeAssignment: userId is required');
      return null;
    }
    
    const assignmentsRef = collection(db, MANUAL_KNIFE_ASSIGNMENTS_COLLECTION);
    // Query without orderBy first to check if any pending assignments exist
    const q = query(
      assignmentsRef,
      where('userId', '==', userId),
      where('status', '==', 'pending')
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    // Get the most recent one (if multiple exist)
    const assignments = [];
    snapshot.forEach((doc) => {
      assignments.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort by assignedAt descending and return the first one
    assignments.sort((a, b) => {
      const timeA = a.assignedAt?.toDate?.()?.getTime() || 0;
      const timeB = b.assignedAt?.toDate?.()?.getTime() || 0;
      return timeB - timeA;
    });
    
    return assignments[0] || null;
  } catch (error) {
    console.error('Error getting user manual knife assignment:', error);
    // If orderBy fails, try without it
    try {
      const assignmentsRef = collection(db, MANUAL_KNIFE_ASSIGNMENTS_COLLECTION);
      const q = query(
        assignmentsRef,
        where('userId', '==', userId),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      }
    } catch (fallbackError) {
      console.error('Fallback query also failed:', fallbackError);
    }
    return null;
  }
};

/**
 * Cancel a manual knife assignment
 * @param {string} assignmentId - Assignment ID
 * @returns {Promise<Object>}
 */
export const cancelManualKnifeAssignment = async (assignmentId) => {
  try {
    const adminInfo = await verifyAdminAccess();
    
    const assignmentRef = doc(db, MANUAL_KNIFE_ASSIGNMENTS_COLLECTION, assignmentId);
    const assignmentDoc = await getDoc(assignmentRef);
    
    if (!assignmentDoc.exists()) {
      throw new Error('Assignment not found');
    }
    
    const assignment = assignmentDoc.data();
    
    if (assignment.status === 'applied') {
      throw new Error('Cannot cancel an already applied assignment');
    }
    
    await updateDoc(assignmentRef, {
      status: 'cancelled',
      cancelledAt: Timestamp.now(),
      cancelledBy: adminInfo.adminId,
      cancelledByEmail: adminInfo.adminEmail,
      updatedAt: Timestamp.now()
    });
    
    // Log admin activity
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'cancelManualKnife',
      targetUserId: assignment.userId,
      targetUserEmail: assignment.userEmail,
      changes: {
        assignmentId,
        knifeAmount: assignment.knifeAmount
      },
      description: `Cancelled manual knife assignment: Amount ${assignment.knifeAmount} USDT`
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error cancelling manual knife assignment:', error);
    throw error;
  }
};

/**
 * Check and apply manual knife when user starts optimization
 * @param {string} userId - User ID
 * @returns {Promise<Object>}
 */
export const checkAndApplyManualKnife = async (userId) => {
  try {
    console.log(`🔍 Checking manual knife for User ${userId} on task submission`);
    
    // Get user data
    const user = await getUserById(userId);
    if (!user) {
      return { applied: false, reason: 'User not found' };
    }
    
    // CRITICAL RULE: Check if user already has an active knife
    // A user can only have ONE active knife at a time
    // Check 1: User object fields
    if (user.isKnifeApplied || user.hasNegativeBalance || (user.balances?.negative && user.balances.negative > 0)) {
      console.log(`  ❌ User already has an active knife applied. Cannot apply new knife.`);
      return { 
        applied: false, 
        reason: 'User already has an active knife. Remove current knife before applying a new one.' 
      };
    }
    
    // Check 2: Active knife transactions in database
    const knifeTransactionsRef = collection(db, 'knifeTransactions');
    const activeKnifeQuery = query(
      knifeTransactionsRef,
      where('userId', '==', userId),
      where('status', '==', 'active')
    );
    const activeKnifeSnapshot = await getDocs(activeKnifeQuery);
    if (!activeKnifeSnapshot.empty) {
      console.log(`  ❌ User already has an active knife transaction. Cannot apply new knife.`);
      return { 
        applied: false, 
        reason: 'User already has an active knife transaction. Remove current knife before applying a new one.' 
      };
    }
    
    // Get pending assignment for this user
    const assignment = await getUserManualKnifeAssignment(userId);
    
    if (!assignment) {
      console.log(`  No pending manual knife assignment found`);
      return { applied: false, reason: 'No pending assignment' };
    }
    
    console.log(`  ✅ Found pending assignment: ${assignment.id}, Amount: ${assignment.knifeAmount} USDT`);

    // NEW SYSTEM: Allow knife amount to exceed balance (negative balance scenario)
    const knifeAmount = assignment.knifeAmount;
    const currentTotal = user.balances?.total || 0;
    const originalBalance = currentTotal; // Store original balance before knife
    
    // Calculate negative amount (difference between knife amount and user balance)
    const negativeAmount = knifeAmount - originalBalance;
    
    // Calculate new balances
    // Wallet shows negative amount (knife amount - original balance)
    const newWallet = -negativeAmount; // This will be negative if knife > balance
    // Total balance = original - knife amount (can be negative)
    const newTotal = originalBalance - knifeAmount;
    // Frozen balance = original balance (what user had before knife)
    const newFrozen = originalBalance;
    
    console.log(`🔪 Applying Manual Knife for User ${userId}:`);
    console.log(`  Knife Amount: ${knifeAmount} USDT`);
    console.log(`  Original Balance: ${originalBalance} USDT`);
    console.log(`  Negative Amount: ${negativeAmount} USDT`);
    console.log(`  Before: Total=${originalBalance}, Wallet=${user.balances?.wallet || 0}`);
    console.log(`  After: Total=${newTotal}, Wallet=${newWallet}, Frozen=${newFrozen}`);
    
    // Update user balances IMMEDIATELY (this is what the user sees)
    await updateUserData(userId, {
      balances: {
        ...user.balances,
        wallet: newWallet,
        frozen: newFrozen,
        negative: negativeAmount, // Store the negative amount (positive number representing deficit)
        total: newTotal,
        originalTotalBeforeKnife: originalBalance,
        originalBalance: originalBalance, // Store original balance
        knifeAmount: knifeAmount,
        negativeAmount: negativeAmount // Explicit negative amount field
      },
      hasNegativeBalance: true,
      isKnifeApplied: true,
      negativeBalanceReason: `Manual knife applied on task submission${assignment.reason ? ` - ${assignment.reason}` : ''}`,
      negativeBalanceAppliedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    // Create knife transaction record
    try {
      const knifeTransactionRef = doc(collection(db, 'knifeTransactions'));
      await setDoc(knifeTransactionRef, {
        userId: userId,
        userEmail: user.email || '',
        knifeAmount: knifeAmount,
        originalBalance: originalBalance,
        negativeAmount: negativeAmount,
        adminId: assignment.assignedBy || 'system',
        adminEmail: assignment.assignedByEmail || '',
        appliedDate: Timestamp.now(),
        removedDate: null,
        profitGenerated: null,
        status: 'active',
        assignmentId: assignment.id,
        vipLevel: user.vipLevel || 'VIP1',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    } catch (txError) {
      console.error('Error creating knife transaction record:', txError);
    }
    
    console.log(`✅ User balance updated in Firestore - changes will be visible immediately`);
    
    // Mark assignment as applied
    await updateDoc(doc(db, MANUAL_KNIFE_ASSIGNMENTS_COLLECTION, assignment.id), {
      status: 'applied',
      appliedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    // Create notification for user
    try {
      const notificationRef = doc(collection(db, 'notifications'));
      await setDoc(notificationRef, {
        userId: userId,
        type: 'warning',
        title: '🔪 Manual Knife Applied',
        message: `The assigned knife of ${knifeAmount} USDT has been applied. Your balance has been adjusted.`,
        read: false,
        createdAt: Timestamp.now()
      });
    } catch (notifError) {
      console.error('Error creating notification:', notifError);
    }
    
    console.log(`✅ Manual knife successfully applied for User ${userId}`);
    
    return {
      applied: true,
      knifeAmount: knifeAmount,
      assignmentId: assignment.id,
      newTotal: newTotal,
      newWallet: newWallet,
      newFrozen: newFrozen,
      originalBalance: originalBalance,
      negativeAmount: negativeAmount
    };
  } catch (error) {
    console.error('Error checking and applying manual knife:', error);
    return { applied: false, error: error.message };
  }
};

/**
 * Remove manual knife from user (restore account access)
 * Calculates profit: 15% of negative amount × VIP level multiplier
 * @param {string} assignmentId - Assignment ID of the applied knife
 * @returns {Promise<Object>}
 */
/**
 * Assign knife to multiple users at once (bulk assignment)
 * @param {Array<string>} userIds - Array of user IDs
 * @param {number} knifeAmount - Amount of knife to apply to each user
 * @param {string} reason - Reason for assignment (optional)
 * @returns {Promise<Object>}
 */
export const assignBulkKnife = async (userIds, knifeAmount, reason = '') => {
  try {
    const adminInfo = await verifyAdminAccess();
    
    if (!userIds || !Array.isArray(userIds) || userIds.length === 0) {
      throw new Error('User IDs array is required');
    }
    
    if (!knifeAmount || knifeAmount <= 0) {
      throw new Error('Knife amount must be greater than 0');
    }

    const results = {
      success: true,
      successCount: 0,
      failedCount: 0,
      errors: []
    };

    // Process each user
    for (const userId of userIds) {
      try {
        await assignManualKnife(userId, knifeAmount, reason);
        results.successCount++;
      } catch (error) {
        results.failedCount++;
        results.errors.push({
          userId,
          error: error.message || 'Unknown error'
        });
        console.error(`Error assigning knife to user ${userId}:`, error);
      }
    }

    // Log admin activity
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'assignBulkKnife',
      changes: {
        userIds: userIds.length,
        knifeAmount,
        successCount: results.successCount,
        failedCount: results.failedCount
      },
      description: `Bulk knife assignment: ${results.successCount} users, Amount ${knifeAmount} USDT per user${reason ? ` - ${reason}` : ''}`
    });

    if (results.failedCount > 0) {
      results.success = false;
      results.error = `${results.failedCount} assignments failed. Check console for details.`;
    }

    return results;
  } catch (error) {
    console.error('Error in bulk knife assignment:', error);
    throw error;
  }
};

export const removeManualKnife = async (assignmentId) => {
  try {
    const adminInfo = await verifyAdminAccess();
    
    // Get assignment
    const assignmentRef = doc(db, MANUAL_KNIFE_ASSIGNMENTS_COLLECTION, assignmentId);
    const assignmentDoc = await getDoc(assignmentRef);
    
    if (!assignmentDoc.exists()) {
      throw new Error('Assignment not found');
    }
    
    const assignment = assignmentDoc.data();
    
    if (assignment.status !== 'applied') {
      throw new Error('Can only remove applied knives');
    }
    
    // Get user data
    const user = await getUserById(assignment.userId);
    if (!user) {
      throw new Error('User not found');
    }
    
    // Check if user actually has negative balance
    if (!user.hasNegativeBalance && (!user.balances?.negative || user.balances.negative >= 0)) {
      throw new Error('User does not have a knife applied');
    }
    
    const originalKnifeAmount = assignment.knifeAmount;
    const originalBalance = user.balances?.originalBalance || user.balances?.originalTotalBeforeKnife || 0;
    const negativeAmount = user.balances?.negativeAmount || (user.balances?.negative || 0);
    
    // If originalBalance is not available, calculate from current state
    // Current total = originalBalance - knifeAmount
    // So: originalBalance = currentTotal + knifeAmount
    const currentTotal = user.balances?.total || 0;
    const calculatedOriginalBalance = originalBalance > 0 
      ? originalBalance 
      : currentTotal + originalKnifeAmount;
    
    // NEW PROFIT CALCULATION: 15% of negative amount × VIP level percentage
    const vipLevel = user.vipLevel || 'VIP1';
    const vipLevelMultipliers = {
      'VIP1': 0.4,  // 0.4%
      'VIP2': 0.6,  // 0.6%
      'VIP3': 0.8,  // 0.8%
      'VIP4': 1.0   // 1.0%
    };
    const vipMultiplier = vipLevelMultipliers[vipLevel] || 0.4;
    
    // Calculate actual negative amount (positive number representing deficit)
    // negativeAmount should be stored as positive number (deficit amount)
    // If not available, calculate: knifeAmount - originalBalance
    let actualNegativeAmount = 0;
    if (negativeAmount > 0) {
      actualNegativeAmount = negativeAmount;
    } else {
      // Calculate from knife amount and original balance
      actualNegativeAmount = Math.max(0, originalKnifeAmount - calculatedOriginalBalance);
    }
    
    // Ensure we have a valid negative amount
    if (actualNegativeAmount <= 0) {
      // Fallback: if knife amount > original balance, the difference is the negative amount
      actualNegativeAmount = Math.max(0, originalKnifeAmount - calculatedOriginalBalance);
    }
    
    // 15% of negative amount
    const fifteenPercentOfNegative = actualNegativeAmount * 0.15;
    
    // Multiply by VIP level multiplier
    const profitAmount = fifteenPercentOfNegative * vipMultiplier;
    
    // Get current todaysProfit
    const currentTodaysProfit = user.balances?.todaysProfit || 0;
    const newTodaysProfit = currentTodaysProfit + profitAmount;
    
    // Calculate new balances
    // Original total + profit amount
    const newTotal = calculatedOriginalBalance + profitAmount;
    const newWallet = newTotal; // Wallet equals new total
    const newFrozen = 0; // Frozen balance reset to 0

    console.log(`🔓 Manual Knife Removal Calculation for User ${assignment.userId}:`);
    console.log(`  Original Knife Amount: ${originalKnifeAmount}`);
    console.log(`  Original Balance: ${calculatedOriginalBalance}`);
    console.log(`  Negative Amount: ${actualNegativeAmount}`);
    console.log(`  15% of Negative: ${fifteenPercentOfNegative}`);
    console.log(`  VIP Level: ${vipLevel} (Multiplier: ${vipMultiplier})`);
    console.log(`  Profit Amount: ${profitAmount}`);
    console.log(`  New Total: ${newTotal}`);
    console.log(`  New Wallet: ${newWallet}`);
    console.log(`  New Frozen: ${newFrozen}`);
    console.log(`  New Today's Profit: ${newTodaysProfit}`);
    
    // Update user balances
    const balanceUpdates = {
      wallet: newWallet,
      frozen: newFrozen,
      total: newTotal,
      negative: 0,
      todaysProfit: newTodaysProfit
    };
    
    // Preserve other balance fields if they exist
    if (user.balances) {
      if (user.balances.commission !== undefined) balanceUpdates.commission = user.balances.commission;
      if (user.balances.salary !== undefined) balanceUpdates.salary = user.balances.salary;
    }
    
    // Clear knife-related fields
    balanceUpdates.originalTotalBeforeKnife = null;
    balanceUpdates.originalBalance = null;
    balanceUpdates.knifeAmount = null;
    balanceUpdates.negativeAmount = null;
    
    // Update user document
    await updateUserData(assignment.userId, {
      balances: balanceUpdates,
      hasNegativeBalance: false,
      isKnifeApplied: false,
      knifeRemovedPendingSubmit: true, // User needs to submit to enable optimization
      negativeBalanceReason: null,
      negativeBalanceAppliedAt: null,
      updatedAt: Timestamp.now()
    });
    
    // Update knife transaction record
    try {
      const knifeTransactionsRef = collection(db, 'knifeTransactions');
      const q = query(
        knifeTransactionsRef,
        where('userId', '==', assignment.userId),
        where('status', '==', 'active'),
        where('assignmentId', '==', assignment.id)
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const transactionDoc = snapshot.docs[0];
        await updateDoc(doc(db, 'knifeTransactions', transactionDoc.id), {
          removedDate: Timestamp.now(),
          profitGenerated: profitAmount,
          status: 'removed',
          removedBy: adminInfo.adminId,
          removedByEmail: adminInfo.adminEmail,
          updatedAt: Timestamp.now()
        });
      }
    } catch (txError) {
      console.error('Error updating knife transaction record:', txError);
    }
    
    console.log('✅ User balance updated in Firestore - knife removed, account restored');
    
    // Update assignment status to "removed"
    await updateDoc(assignmentRef, {
      status: 'removed',
      removedAt: Timestamp.now(),
      removedBy: adminInfo.adminId,
      removedByEmail: adminInfo.adminEmail,
      profitAmount: profitAmount,
      updatedAt: Timestamp.now()
    });
    
    // Create notification for user
    try {
      const notificationRef = doc(collection(db, 'notifications'));
      await setDoc(notificationRef, {
        userId: assignment.userId,
        type: 'success',
        title: '🔓 Knife Removed - Account Restored',
        message: `The knife of ${originalKnifeAmount} USDT has been removed. Profit of ${profitAmount.toFixed(2)} USDT (15% of negative × ${vipLevel} multiplier) has been added to your today's profit. Your account access has been restored.`,
        read: false,
        createdAt: Timestamp.now()
      });
    } catch (notifError) {
      console.error('Error creating notification:', notifError);
    }
    
    // Log admin activity
    await logAdminActivity({
      adminId: adminInfo.adminId,
      adminEmail: adminInfo.adminEmail,
      action: 'removeManualKnife',
      targetUserId: assignment.userId,
      targetUserEmail: assignment.userEmail,
      changes: {
        assignmentId,
        originalKnifeAmount,
        negativeAmount: actualNegativeAmount,
        profitAmount,
        vipLevel,
        newTotal,
        newWallet,
        newFrozen,
        newTodaysProfit
      },
      description: `Removed manual knife from ${assignment.userEmail || assignment.userId}. Profit: ${profitAmount.toFixed(2)} USDT (15% of negative × ${vipLevel} multiplier) added to today's profit. Account access restored.`
    });
    
    console.log(`✅ Manual knife successfully removed for User ${assignment.userId}`);
    
    return {
      success: true,
      originalKnifeAmount,
      negativeAmount: actualNegativeAmount,
      profitAmount,
      vipLevel,
      newTotal,
      newWallet,
      newFrozen,
      newTodaysProfit,
      originalBalance: calculatedOriginalBalance
    };
  } catch (error) {
    console.error('Error removing manual knife:', error);
    throw error;
  }
};

